// Field schema configs for each CMS collection.
// type: 'text' | 'textarea' | 'number' | 'toggle' | 'url'

export const COLLECTION_LABELS = {
  // Home
  services: 'Services',
  industries: 'Industries',
  portfolioCategories: 'Portfolio Categories',
  portfolioItems: 'Portfolio Items',
  caseStudies: 'Case Studies',
  valuesItems: 'Our Values',
  technologies: 'Technologies',
  testimonials: 'Testimonials',
  clients: 'Clients',
  // Company
  teamMembers: 'Team Members',
  jobs: 'Job Openings',
  reviews: 'Reviews',
  // Services page
  serviceCards: 'Service Cards',
  serviceIndustries: 'Service Industries',
  recentProjects: 'Recent Projects',
  // Case Studies page
  caseStudyCategories: 'Case Study Categories',
  // Why MGS page
  whyMgsBlocks: 'Editorial Blocks',
  certifications: 'Certifications',
  // Detail pages
  servicePages: 'Service Detail Pages',
  industryPages: 'Industry Detail Pages',
  // Legal
  pages: 'Pages (Legal / Policies / Custom)',
};

export const COLLECTION_GROUPS = [
  {
    label: 'Home',
    description: 'Content shown on the homepage',
    collections: ['services', 'industries', 'portfolioCategories', 'portfolioItems', 'caseStudies', 'valuesItems', 'technologies', 'testimonials', 'clients'],
  },
  {
    label: 'Company',
    description: 'Team, careers and reviews',
    collections: ['teamMembers', 'jobs', 'reviews'],
  },
  {
    label: 'Services Page',
    description: 'Sections on the /services page',
    collections: ['serviceCards', 'serviceIndustries', 'recentProjects'],
  },
  {
    label: 'Case Studies Page',
    description: 'Categories used to filter case studies',
    collections: ['caseStudyCategories'],
  },
  {
    label: 'Why MGS Page',
    description: 'Editorial blocks and certifications on /why-mgs',
    collections: ['whyMgsBlocks', 'certifications'],
  },
  {
    label: 'Detail Pages',
    description: 'Per-slug service and industry landing pages',
    collections: ['servicePages', 'industryPages'],
  },
  {
    label: 'Legal',
    description: 'Privacy, terms and policy pages',
    collections: ['pages'],
  },
];

export const COLLECTION_SCHEMAS = {
  services: [
    { key: 'title', label: 'Title', type: 'text', required: true },
    { key: 'desc', label: 'Description', type: 'textarea', required: true },
    { key: 'icon', label: 'Icon (Lucide name)', type: 'text' },
    { key: 'order', label: 'Order', type: 'number' },
    { key: 'visible', label: 'Visible', type: 'toggle' },
  ],
  industries: [
    { key: 'title', label: 'Title', type: 'text', required: true },
    { key: 'desc', label: 'Description', type: 'textarea', required: true },
    { key: 'icon', label: 'Icon (Lucide name)', type: 'text' },
    { key: 'order', label: 'Order', type: 'number' },
    { key: 'visible', label: 'Visible', type: 'toggle' },
  ],
  testimonials: [
    { key: 'name',      label: 'Name',                       type: 'text',     required: true },
    { key: 'role',      label: 'Role / Title',               type: 'text' },
    { key: 'company',   label: 'Company',                    type: 'text' },
    { key: 'location',  label: 'Location',                   type: 'text' },
    { key: 'quote',     label: 'Quote (text feedback)',       type: 'textarea', required: true },
    { key: 'avatar',    label: 'Avatar URL',                  type: 'url', mediaPicker: true },
    { key: 'video_url', label: 'Video URL (YouTube / direct)',type: 'url' },
    { key: 'rating',    label: 'Star Rating (1–5)',           type: 'number' },
    { key: 'order',     label: 'Order',                       type: 'number' },
    { key: 'visible',   label: 'Visible',                     type: 'toggle' },
  ],
  clients: [
    { key: 'name', label: 'Client Name', type: 'text', required: true },
    { key: 'logo_url', label: 'Logo URL', type: 'url', mediaPicker: true },
    { key: 'website', label: 'Website', type: 'url' },
    { key: 'order', label: 'Order', type: 'number' },
    { key: 'visible', label: 'Visible', type: 'toggle' },
  ],
  teamMembers: [
    { key: 'name', label: 'Full Name', type: 'text', required: true },
    { key: 'role', label: 'Role / Title', type: 'text', required: true },
    { key: 'bio', label: 'Bio', type: 'textarea' },
    { key: 'photo', label: 'Photo URL', type: 'url', mediaPicker: true },
    { key: 'linkedin', label: 'LinkedIn URL', type: 'url' },
    { key: 'order', label: 'Order', type: 'number' },
    { key: 'visible', label: 'Visible', type: 'toggle' },
  ],
  jobs: [
    { key: 'title', label: 'Job Title', type: 'text', required: true },
    { key: 'category', label: 'Category (Engineering / Design / Product / Marketing / Operations)', type: 'text', required: true },
    { key: 'location', label: 'Location (e.g. Remote · Worldwide)', type: 'text' },
    { key: 'type', label: 'Type (Full-time / Part-time / Contract)', type: 'text' },
    { key: 'description', label: 'Description', type: 'textarea', required: true },
    { key: 'applyLink', label: 'Apply URL (external link or leave blank for /contact)', type: 'url' },
    { key: 'order', label: 'Order', type: 'number' },
    { key: 'visible', label: 'Visible', type: 'toggle' },
  ],
  portfolioItems: [
    { key: 'title', label: 'Title', type: 'text', required: true },
    { key: 'tag', label: 'Tag (short tech / type label)', type: 'text' },
    { key: 'category', label: 'Category (web / mobile / ai / ecom / biz / uiux / games)', type: 'text', required: true },
    { key: 'img', label: 'Image URL', type: 'url', mediaPicker: true },
    { key: 'link', label: 'Live Project URL (optional)', type: 'url' },
    { key: 'caseStudySlug', label: 'Linked Case Study Slug (optional)', type: 'text' },
    { key: 'tags', label: 'Tech Tags', type: 'json',
      subfields: [
        { key: 'name', label: 'Tag Name (e.g. Next.js)', type: 'text', required: true },
      ],
    },
    { key: 'order', label: 'Order', type: 'number' },
    { key: 'visible', label: 'Visible', type: 'toggle' },
  ],
  caseStudies: [
    { key: 'slug', label: 'Slug (URL identifier, e.g. ai-lead-qualification)', type: 'text', required: true },
    { key: 'brand', label: 'Brand Name', type: 'text', required: true },
    { key: 'title', label: 'Title', type: 'textarea', required: true },
    { key: 'category', label: 'Category (Development / FinTech / UX Engineering / Technology / Creative / AI & ML)', type: 'text' },
    { key: 'date', label: 'Date (display string, e.g. "September 14, 2023")', type: 'text' },
    { key: 'read_time', label: 'Read Time (e.g. "5 Minute Read")', type: 'text' },
    { key: 'img', label: 'Hero Image URL', type: 'url', mediaPicker: true },
    { key: 'client_logo', label: 'Client Logo URL', type: 'url', mediaPicker: true },
    { key: 'metric', label: 'Key Metric (badge on card)', type: 'text' },
    { key: 'gallery', label: 'Gallery Images', type: 'json',
      subfields: [
        { key: 'url', label: 'Image URL', type: 'url', mediaPicker: true, required: true },
      ],
    },
    { key: 'tech_stack', label: 'Tech Stack', type: 'json',
      subfields: [
        { key: 'name', label: 'Technology Name', type: 'text', required: true },
        { key: 'icon_url', label: 'Icon URL (optional)', type: 'url' },
      ],
    },
    { key: 'clients_vision', label: 'Clients Vision (paragraph)', type: 'textarea' },
    { key: 'challenges', label: 'Challenges', type: 'json',
      subfields: [
        { key: 'title', label: 'Title', type: 'text', required: true },
        { key: 'desc', label: 'Description', type: 'textarea', required: true },
      ],
    },
    { key: 'solution', label: 'Solution', type: 'json',
      subfields: [
        { key: 'title', label: 'Title', type: 'text', required: true },
        { key: 'desc', label: 'Description', type: 'textarea', required: true },
      ],
    },
    { key: 'results', label: 'Results / Final Thoughts (paragraph)', type: 'textarea' },
    { key: 'order', label: 'Order', type: 'number' },
    { key: 'visible', label: 'Visible', type: 'toggle' },
  ],
  caseStudyCategories: [
    { key: 'label', label: 'Label', type: 'text', required: true },
    { key: 'order', label: 'Order', type: 'number' },
    { key: 'visible', label: 'Visible', type: 'toggle' },
  ],
  technologies: [
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'img', label: 'Icon URL', type: 'url', mediaPicker: true },
    { key: 'category', label: 'Category (ai / frontend / backend / mobile / ecom / db)', type: 'text', required: true },
    { key: 'order', label: 'Order', type: 'number' },
    { key: 'visible', label: 'Visible', type: 'toggle' },
  ],
  reviews: [
    { key: 'name', label: 'Reviewer Name', type: 'text', required: true },
    { key: 'role', label: 'Reviewer Role / Title', type: 'text' },
    { key: 'company', label: 'Company', type: 'text' },
    { key: 'location', label: 'Location (e.g. Dubai, UAE)', type: 'text' },
    { key: 'avatar', label: 'Avatar / Photo URL', type: 'url', mediaPicker: true },
    { key: 'videoUrl', label: 'Video URL (YouTube / Vimeo — optional, makes this a Featured video testimonial)', type: 'url' },
    { key: 'rating', label: 'Rating (1–5)', type: 'number' },
    { key: 'quote', label: 'Review / Quote Text', type: 'textarea', required: true },
    { key: 'order', label: 'Order', type: 'number' },
    { key: 'visible', label: 'Visible', type: 'toggle' },
  ],
  pages: [
    { key: 'slug', label: 'URL Slug (lowercase, hyphens only — used in /p/{slug})', type: 'text', required: true },
    { key: 'title', label: 'Title', type: 'text', required: true },
    { key: 'body', label: 'Body (multi-paragraph plain text)', type: 'textarea', rows: 14, required: true },
    { key: 'metaTitle', label: 'SEO Title (browser tab + Google result)', type: 'text' },
    { key: 'metaDescription', label: 'SEO Description (1-2 lines)', type: 'textarea' },
    { key: 'order', label: 'Order', type: 'number' },
    { key: 'visible', label: 'Visible', type: 'toggle' },
  ],
  valuesItems: [
    { key: 'title', label: 'Title', type: 'text', required: true },
    { key: 'desc', label: 'Description', type: 'textarea' },
    { key: 'icon', label: 'Icon (Lucide name, e.g. Users / Award / Handshake / Sparkles)', type: 'text' },
    { key: 'img', label: 'Image URL (Unsplash or royalty-free)', type: 'url', mediaPicker: true },
    { key: 'accent', label: 'Accent Colour (hex, e.g. #2F6FB0)', type: 'text' },
    { key: 'order', label: 'Order', type: 'number' },
    { key: 'visible', label: 'Visible', type: 'toggle' },
  ],
  portfolioCategories: [
    { key: 'category_id', label: 'Category ID', type: 'text', required: true },
    { key: 'label', label: 'Label', type: 'text', required: true },
    { key: 'icon', label: 'Icon (Lucide name)', type: 'text' },
    { key: 'order', label: 'Order', type: 'number' },
    { key: 'visible', label: 'Visible', type: 'toggle' },
  ],
  serviceCards: [
    { key: 'slug', label: 'URL Slug (matches the Service Detail Page it opens, e.g. machine-learning)', type: 'text' },
    { key: 'title', label: 'Title', type: 'text', required: true },
    { key: 'desc', label: 'Description', type: 'textarea' },
    { key: 'icon', label: 'Icon (Lucide name)', type: 'text' },
    { key: 'image', label: 'Image URL', type: 'url', mediaPicker: true },
    { key: 'highlighted', label: 'Highlighted (large feature card)', type: 'toggle' },
    { key: 'order', label: 'Order', type: 'number' },
    { key: 'visible', label: 'Visible', type: 'toggle' },
  ],
  serviceIndustries: [
    { key: 'slug', label: 'URL Slug (matches the Industry Detail Page it opens, e.g. fintech)', type: 'text' },
    { key: 'label', label: 'Label', type: 'text', required: true },
    { key: 'icon', label: 'Icon (Lucide name)', type: 'text' },
    { key: 'highlighted', label: 'Highlighted (gold gradient)', type: 'toggle' },
    { key: 'order', label: 'Order', type: 'number' },
    { key: 'visible', label: 'Visible', type: 'toggle' },
  ],
  recentProjects: [
    { key: 'title', label: 'Title', type: 'text', required: true },
    { key: 'image', label: 'Image URL', type: 'url', mediaPicker: true },
    { key: 'tech_stack', label: 'Tech Stack', type: 'text' },
    { key: 'link', label: 'Project Link', type: 'url' },
    { key: 'order', label: 'Order', type: 'number' },
    { key: 'visible', label: 'Visible', type: 'toggle' },
  ],
  whyMgsBlocks: [
    { key: 'heading', label: 'Heading', type: 'text', required: true },
    { key: 'body', label: 'Body Paragraph', type: 'textarea', required: true },
    { key: 'image', label: 'Image URL', type: 'url', mediaPicker: true },
    { key: 'imageSide', label: 'Image Side (left / right)', type: 'text' },
    { key: 'order', label: 'Order', type: 'number' },
    { key: 'visible', label: 'Visible', type: 'toggle' },
  ],
  certifications: [
    { key: 'name', label: 'Certification Name', type: 'text', required: true },
    { key: 'logo', label: 'Logo URL', type: 'url', mediaPicker: true },
    { key: 'order', label: 'Order', type: 'number' },
    { key: 'visible', label: 'Visible', type: 'toggle' },
  ],
  servicePages: [
    { key: 'slug',             label: 'URL Slug (e.g. generative-ai-chatbots)',          type: 'text',     required: true },
    { key: 'title',            label: 'Page Title',                                       type: 'text',     required: true },
    { key: 'crumb',            label: 'Breadcrumb Label (short)',                          type: 'text' },
    { key: 'tagline',          label: 'Hero Tagline (1–2 sentences)',                      type: 'textarea' },
    { key: 'hero_image',       label: 'Hero / Overview Image URL',                        type: 'url', mediaPicker: true },
    { key: 'overview_heading', label: 'Overview Section Heading',                         type: 'text' },
    { key: 'overview_body',    label: 'Overview Body (separate paragraphs with blank line)', type: 'textarea', rows: 8 },
    { key: 'metrics',          label: 'Metrics',        type: 'json',
      subfields: [
        { key: 'value', label: 'Value (e.g. 200+)',    type: 'text', required: true },
        { key: 'label', label: 'Label (e.g. Projects)', type: 'text', required: true },
      ],
    },
    { key: 'features',         label: 'Features',       type: 'json',
      subfields: [
        { key: 'icon',   label: 'Icon (Lucide name, e.g. Brain)',    type: 'text' },
        { key: 'title',  label: 'Title',                             type: 'text',     required: true },
        { key: 'desc',   label: 'Description',                       type: 'textarea', required: true },
        { key: 'accent', label: 'Accent Colour (hex, e.g. #2F6FB0)', type: 'text' },
      ],
    },
    { key: 'process_steps',    label: 'Process Steps',  type: 'json',
      subfields: [
        { key: 'step',  label: 'Step Number (e.g. 01)', type: 'text', required: true },
        { key: 'title', label: 'Title',                  type: 'text', required: true },
        { key: 'desc',  label: 'Description',            type: 'textarea', required: true },
      ],
    },
    { key: 'use_cases',        label: 'Use Cases',      type: 'json',
      subfields: [
        { key: 'icon',     label: 'Icon (Lucide name)',  type: 'text' },
        { key: 'industry', label: 'Industry / Title',    type: 'text',     required: true },
        { key: 'desc',     label: 'Description',         type: 'textarea', required: true },
      ],
    },
    { key: 'tech_stack',       label: 'Tech Stack',     type: 'json',
      subfields: [
        { key: 'name',  label: 'Technology Name',          type: 'text', required: true },
        { key: 'color', label: 'Chip Colour (hex, optional)', type: 'text' },
      ],
    },
    { key: 'benefits',         label: 'Why MGS Benefits', type: 'json',
      subfields: [
        { key: 'icon',  label: 'Icon (Lucide name, e.g. Shield)', type: 'text' },
        { key: 'title', label: 'Title',                             type: 'text',     required: true },
        { key: 'desc',  label: 'Description',                       type: 'textarea', required: true },
      ],
    },
    { key: 'metaTitle',        label: 'SEO Title',                                        type: 'text' },
    { key: 'metaDescription',  label: 'SEO Description',                                  type: 'textarea' },
    { key: 'order',            label: 'Order',                                            type: 'number' },
    { key: 'visible',          label: 'Visible',                                          type: 'toggle' },
  ],
  industryPages: [
    { key: 'slug',             label: 'URL Slug (e.g. fintech)',                          type: 'text',     required: true },
    { key: 'title',            label: 'Page Title',                                       type: 'text',     required: true },
    { key: 'tagline',          label: 'Hero Tagline (1–2 sentences)',                      type: 'textarea' },
    { key: 'hero_image',       label: 'Hero Image URL',                                   type: 'url', mediaPicker: true },
    { key: 'badge',            label: 'Badge (short label)',                              type: 'text' },
    { key: 'overview_heading', label: 'Overview Section Heading',                         type: 'text' },
    { key: 'overview_body',    label: 'Overview Body (separate paragraphs with blank line)', type: 'textarea', rows: 8 },
    { key: 'overview_stats',   label: 'Overview Stats',  type: 'json',
      subfields: [
        { key: 'value', label: 'Value (e.g. 200+)',    type: 'text', required: true },
        { key: 'label', label: 'Label (e.g. Projects)', type: 'text', required: true },
      ],
    },
    { key: 'challenges',       label: 'Challenges',    type: 'json',
      subfields: [
        { key: 'icon',  label: 'Icon (Lucide name)',     type: 'text' },
        { key: 'title', label: 'Title',                  type: 'text',     required: true },
        { key: 'desc',  label: 'Description',            type: 'textarea', required: true },
      ],
    },
    { key: 'solutions',        label: 'Solutions',     type: 'json',
      subfields: [
        { key: 'icon',   label: 'Icon (Lucide name)',     type: 'text' },
        { key: 'title',  label: 'Title',                  type: 'text',     required: true },
        { key: 'desc',   label: 'Description',            type: 'textarea', required: true },
        { key: 'accent', label: 'Accent Colour (hex)',   type: 'text' },
      ],
    },
    { key: 'metrics',          label: 'Metrics',       type: 'json',
      subfields: [
        { key: 'value', label: 'Value (e.g. 200+)',    type: 'text', required: true },
        { key: 'label', label: 'Label (e.g. Projects)', type: 'text', required: true },
      ],
    },
    { key: 'process_steps',    label: 'Process Steps', type: 'json',
      subfields: [
        { key: 'step',  label: 'Step Number (e.g. 01)', type: 'text', required: true },
        { key: 'title', label: 'Title',                  type: 'text', required: true },
        { key: 'desc',  label: 'Description',            type: 'textarea', required: true },
      ],
    },
    { key: 'tech_stack',       label: 'Tech Stack',    type: 'json',
      subfields: [
        { key: 'name',  label: 'Technology Name',          type: 'text', required: true },
        { key: 'color', label: 'Chip Colour (hex, optional)', type: 'text' },
      ],
    },
    { key: 'related',          label: 'Related Industry Slugs', type: 'text' },
    { key: 'order',            label: 'Order',                                            type: 'number' },
    { key: 'visible',          label: 'Visible',                                          type: 'toggle' },
  ],
};
